import type { CharacterManifest } from "@arena/shared";

/** The role written on a champion's card. */
const ROLES: Record<CharacterManifest["archetype"], string> = {
  brawler: "Lottatore",
  mobile: "Agile",
  zoner: "Da distanza",
  tank: "Resistente",
  trickster: "Imprevedibile",
};

/** Under the name: when they lived and what they are called, then a line of theirs. */
interface Caption {
  readonly lived: string;
  readonly quote: string;
}

/**
 * Real words where they fit the fighter, else a line in their voice. A
 * champion without one shows only its name and moves.
 */
const CAPTIONS: Readonly<Record<string, Caption>> = {
  dante: { lived: "1265 – 1321 · il Sommo Poeta", quote: "Lasciate ogni speranza, voi ch'entrate." },
  gandhi: { lived: "1869 – 1948 · il Mahatma", quote: "Sono tornato. E mi girano parecchio." },
  mussolini: { lived: "1883 – 1945 · il Duce", quote: "Governare gli italiani non è difficile: è inutile." },
  kafka: { lived: "1883 – 1924 · lo scrittore di Praga", quote: "Un libro dev'essere l'ascia per il mare gelato dentro di noi." },
  lovecraft: { lived: "1890 – 1937 · il solitario di Providence", quote: "L'emozione più antica e più forte dell'umanità è la paura." },
  auramennone: { lived: "XII secolo a.C. · re di Micene, re dei re", quote: "Io non combatto: io poso." },
  cleopatra: { lived: "69 – 30 a.C. · l'ultima regina d'Egitto", quote: "Non sarò portata in trionfo." },
  antonietta: { lived: "1755 – 1793 · regina di Francia", quote: "Che mangino brioche!" },
  giovanna: { lived: "1412 – 1431 · la Pulzella d'Orléans", quote: "Non ho paura: sono nata per questo." },
};

/** Where the face is in each close-up (fractions of its side): the card's photo centres on it. */
const FACES: Readonly<Record<string, readonly [number, number]>> = {
  dante: [0.55, 0.4],
  gandhi: [0.55, 0.3],
  mussolini: [0.47, 0.3],
  kafka: [0.52, 0.4],
  lovecraft: [0.48, 0.45],
  auramennone: [0.55, 0.45],
  cleopatra: [0.42, 0.42],
  antonietta: [0.43, 0.48],
  giovanna: [0.45, 0.38],
};

/**
 * A champion's card, as a statue's card in the museum shows it (D49): the
 * portrait, the role, name, dates and a line of theirs, then the moves by name
 * with the button that throws each, and nothing else.
 */
export function championCard(c: CharacterManifest): HTMLElement[] {
  const photo = document.createElement("figure");
  photo.className = "hero-photo";
  const image = document.createElement("img");
  image.src = `/proto2d/portraits/closeups/${c.id}.png`;
  image.alt = "";
  image.draggable = false;
  const [faceX, faceY] = FACES[c.id] ?? [0.5, 0.35];
  image.style.setProperty("--face-x", String(faceX));
  image.style.setProperty("--face-y", String(faceY));
  const role = document.createElement("span");
  role.className = "hero-role";
  role.textContent = ROLES[c.archetype];
  photo.append(image, role);

  const name = document.createElement("h3");
  name.className = "hero-name";
  name.textContent = c.displayName;
  const parts: HTMLElement[] = [photo, name];
  const caption = CAPTIONS[c.id];
  if (caption) {
    const lived = document.createElement("p");
    lived.className = "hero-lived";
    lived.textContent = caption.lived;
    const quote = document.createElement("blockquote");
    quote.className = "hero-quote";
    quote.textContent = `«${caption.quote}»`;
    parts.push(lived, quote);
  }

  const m = c.moveset;
  const moves = document.createElement("ul");
  moves.className = "hero-moves";
  const rows: [string, string | undefined, boolean?][] = [
    ["A A A", [m.light1.name, m.light2.name, m.light3.name].join(" · ")],
    ["Salto A", m.airAttack?.name],
    ["Scatto A", m.dashAttack?.name],
    ["S1", m.special1?.name],
    ["S2", m.special2?.name],
    ["★ Suprema", m.superSlam.name, true],
  ];
  for (const [key, move, supreme] of rows) {
    if (!move) continue;
    const item = document.createElement("li");
    item.classList.toggle("supreme", supreme === true);
    const button = document.createElement("kbd");
    button.textContent = key;
    const label = document.createElement("span");
    label.textContent = move;
    item.append(button, label);
    moves.append(item);
  }
  parts.push(moves);
  return parts;
}
